import { cn } from "../lib/utils";
import React, { useRef, useState } from "react";
import { motion } from "motion/react";
import { IconUpload } from "@tabler/icons-react";
import { useDropzone } from "react-dropzone";
import Alerta from "./Alerta";
import { AnimatePresence } from "framer-motion";

const mainVariant = {
  initial: { x: 0, y: 0 },
  animate: { x: 20, y: -20, opacity: 0.9 },
};

const secondaryVariant = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
};

export const FileUpload = ({ onChange, etiqueta, requisitosDocumentoCompleto, numero, numeroAfiliado }) => {
  const esObligatorio = requisitosDocumentoCompleto?.es_obligatorio !== false;
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  const isMultiLoad = requisitosDocumentoCompleto?.isMultiLoad;
  const [alerta, setAlerta] = useState(null);

  const MIME_TYPES = {
    "PDF": { mime: "application/pdf", ext: [".pdf"] },
    "XML": { mime: "text/xml", ext: [".xml"] }, // text/xml o application/xml
    "JPG": { mime: "image/jpeg", ext: [".jpg", ".jpeg"] },
    "JPEG": { mime: "image/jpeg", ext: [".jpg", ".jpeg"] },
    "PNG": { mime: "image/png", ext: [".png"] }
  };

  const tipoArchivoRaw = requisitosDocumentoCompleto?.tipo_archivo || "";

  // Parsear tipos configurados (ej: "PDF, XML")
  const tiposPermitidos = tipoArchivoRaw
    .toUpperCase()
    .split(",")
    .map(t => t.trim())
    .filter(t => t);

  // Construir objeto accept para Dropzone
  const acceptMIMEs = {};
  const extensionesPermitidas = [];

  if (tiposPermitidos.length > 0) {
    tiposPermitidos.forEach(tipo => {
      const config = MIME_TYPES[tipo];
      if (config) {
        // Agrupar extensiones por MIME type
        if (acceptMIMEs[config.mime]) {
          acceptMIMEs[config.mime] = [...new Set([...acceptMIMEs[config.mime], ...config.ext])];
        } else {
          acceptMIMEs[config.mime] = config.ext;
        }
        extensionesPermitidas.push(...config.ext);
      }
    });

    // Fallback para XML si se necesita application/xml también
    if (tiposPermitidos.includes("XML")) {
      acceptMIMEs["application/xml"] = [".xml"];
    }
  } else {
    // Default fallback si no hay configuración (comportamiento previo o asumiendo imagen/pdf)
    acceptMIMEs["image/*"] = [];
    acceptMIMEs["application/pdf"] = [".pdf"];
    extensionesPermitidas.push(".jpg", ".png", ".pdf");
  }

  // String para input accept (ej: ".pdf,.xml,.jpg")
  const acceptAttr = extensionesPermitidas.join(",");

  function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0,
        v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }


  const sendBase64ToApi = async (base64, filename, overrideId = null) => {
    const nombrePrestamo = requisitosDocumentoCompleto.tipo_prestamo.nombre;
    const tipoDocumentoId = requisitosDocumentoCompleto.id_tipo_documento;

    try {
      await fetch(
        `${apiUrl}/upload-base64?numero=${numero}&nombre_prestamo=${nombrePrestamo}&tipo_documento_id=${tipoDocumentoId}&codigo_afiliado=${numeroAfiliado}&es_complementario=false`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ base64, filename }),
        }
      );
    } catch (error) {
      console.error("❌ Error al enviar base64", error);
    }
  };

  const handleFileChange = (newFiles, overrideId = null) => {
    const isMulti = overrideId ? true : isMultiLoad;
    const maxArchivos = isMulti ? 999 : (requisitosDocumentoCompleto.partes?.length || 1);
    const nuevosArchivos = Array.from(newFiles || []);
    
    // Obtener límite de tamaño desde variables de entorno (.env) o usar 10 MB por defecto
    const maxFileSizeMb = Number(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 10;
    const MAX_FILE_SIZE = maxFileSizeMb * 1024 * 1024;

    // 1. Validar tipos de archivo
    const validosPorTipo = nuevosArchivos.filter((file) => {
      // Validar extensión
      const ext = "." + file.name.split('.').pop().toLowerCase();
      return extensionesPermitidas.includes(ext) || Object.keys(acceptMIMEs).some(mime => {
        if (mime.endsWith("/*")) {
          const base = mime.split("/")[0];
          return file.type.startsWith(base + "/");
        }
        return file.type === mime;
      });
    });

    if (validosPorTipo.length === 0 && nuevosArchivos.length > 0) {
      setAlerta({ tipo: "warning", mensaje: `Solo se permiten archivos: ${tiposPermitidos.join(", ")}` });
      return;
    }

    // 2. Validar tamaño de archivo (máximo dinámico desde .env)
    const filtrados = validosPorTipo.filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        setAlerta({ tipo: "warning", mensaje: `El archivo "${file.name}" supera el límite de tamaño permitido (máximo ${maxFileSizeMb} MB).` });
        return false;
      }
      return true;
    });

    if (filtrados.length === 0) {
      return;
    }

    const combinados = [...files, ...filtrados];
    const unicosPorNombre = Array.from(new Map(combinados.map((f) => [f.name, f])).values()).slice(-maxArchivos);

    setFiles(unicosPorNombre);
    onChange && onChange(unicosPorNombre);

    filtrados.forEach((file) => logFileInfo(file, overrideId));
  };

  const logFileInfo = (file, overrideId = null) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result;
      const uuid = generateUUID();
      const uniqueName = `${uuid}_${file.name}`;
      sendBase64ToApi(base64, uniqueName, overrideId);
    };
    reader.readAsDataURL(file);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const { getRootProps, isDragActive } = useDropzone({
    multiple: false,
    noClick: true,
    onDrop: handleFileChange,
    accept: acceptMIMEs,
    onDropRejected: () => {
      setAlerta({ tipo: "warning", mensaje: `Archivo no aceptado. Permitido: ${tiposPermitidos.join(", ")}` });
    },
  });

  const partes = requisitosDocumentoCompleto.partes || [];
  const parteActual = partes[files.length] || partes[partes.length - 1];

  return (
    <div className="w-full">
      {/* Mini-stepper horizontal de partes cuando son más de 1 */}
      {partes.length > 1 && (
        <div data-tour="indicador-partes" className="mb-4 sm:mb-8 max-w-xl mx-auto px-2">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center mb-4 sm:mb-6">
            Partes requeridas para {etiqueta}
          </p>
          <div className="flex items-center justify-between relative mt-4">
            {/* Línea de fondo del stepper */}
            <div className="absolute top-[18px] left-0 right-0 h-1 bg-slate-100 -translate-y-1/2 z-0 rounded-full" />
            {/* Línea de progreso activa */}
            <div
              className="absolute top-[18px] left-0 h-1 bg-primary -translate-y-1/2 z-0 rounded-full transition-all duration-500"
              style={{
                width: `${(files.length / (partes.length - 1)) * 100}%`,
                maxWidth: '100%'
              }}
            />

            {partes.map((parte, idx) => {
              const fileSubido = files[idx];
              const isActive = idx === files.length;
              const isCompleted = !!fileSubido;

              return (
                <div
                  key={parte.id_requisito_documento_parte}
                  className="flex flex-col items-center z-10 relative"
                >
                  {/* Círculo del paso */}
                  <div
                    className={cn(
                      "size-9 rounded-full flex items-center justify-center font-black text-xs transition-all duration-300 border-2 bg-white",
                      isCompleted
                        ? "bg-green-500 border-green-500 text-white shadow-md shadow-green-500/20"
                        : isActive
                          ? "border-primary text-primary shadow-md shadow-primary/10 ring-4 ring-primary/10 scale-110"
                          : "border-slate-200 text-slate-400"
                    )}
                  >
                    {isCompleted ? "✓" : idx + 1}
                  </div>
                  {/* Etiqueta del paso - Se oculta en móviles para evitar amontonarse */}
                  <span
                    className={cn(
                      "hidden sm:inline-block text-[9px] font-extrabold uppercase tracking-wider mt-3 bg-white px-2.5 py-1 rounded-lg border shadow-sm transition-all whitespace-nowrap",
                      isCompleted
                        ? "text-green-600 border-green-100"
                        : isActive
                          ? "text-primary border-primary/20 font-black scale-105"
                          : "text-slate-400 border-slate-100"
                    )}
                  >
                    {parte.nombre_parte} {parte.es_obligatorio && "*"}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Indicador de paso actual - Visible solo para pantallas de móviles */}
          <div className="sm:hidden text-center mt-3">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/5 text-primary border border-primary/10 rounded-xl text-[10px] font-extrabold uppercase tracking-wider shadow-sm">
              <span className="opacity-80">Paso {Math.min(files.length + 1, partes.length)} de {partes.length}:</span>
              <span className="font-black">{(partes[files.length] || partes[partes.length - 1])?.nombre_parte}</span>
            </span>
          </div>
        </div>
      )}

      {/* Caso de 1 sola parte básica o sin partes */}
      {partes.length <= 1 && partes.length > 0 && (
        <div data-tour="indicador-partes" className="mb-4 sm:mb-6 max-w-xl mx-auto text-center">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 sm:mb-2">Requisito</p>
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-black text-slate-700 shadow-sm">
            📄 {partes[0].nombre_parte} {partes[0].es_obligatorio ? "(Obligatorio)" : ""}
          </span>
        </div>
      )}

      <div {...getRootProps()}>
        <motion.div
          onClick={handleClick}
          whileHover="animate"
          className={`p-4 sm:p-8 group/file block rounded-2xl sm:rounded-[2.5rem] border-2 border-dashed cursor-pointer w-full relative overflow-hidden max-w-xl mx-auto shadow-sm hover:shadow-xl transition-all duration-300 bg-white ${
            esObligatorio
              ? "border-slate-200 hover:border-primary/30 hover:shadow-slate-100/50"
              : "border-slate-200 hover:border-emerald-300 hover:shadow-emerald-100/30"
          }`}
        >
          <input
            ref={fileInputRef}
            id="file-upload-handle"
            type="file"
            onChange={(e) => handleFileChange(e.target.files)}
            className="hidden"
            accept={acceptAttr}
          />

          <div className="absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,white,transparent)]" />

          <div className="flex flex-col items-center justify-center -mt-8 sm:-mt-6 w-full">
            <div className="relative z-20 flex flex-col items-center gap-1 sm:gap-2 mb-2 sm:mb-4 w-full">
              <span className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-slate-400">Paso de Carga</span>

              <div className="flex flex-col items-center gap-1 sm:gap-2 w-full">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  {esObligatorio ? "Debes subir el documento:" : "Sube el documento si lo tienes disponible:"}
                </span>

                <div className="flex flex-col items-center gap-1.5 w-full px-2">
                  {parteActual && partes.length > 1 ? (
                    <p className={`text-base sm:text-lg font-black uppercase tracking-wide text-center max-w-md leading-snug ${
                      esObligatorio ? "text-primary" : "text-emerald-700"
                    }`} style={{ overflowWrap: "break-word", wordBreak: "normal" }}>
                      {etiqueta}: <span className={esObligatorio ? "text-slate-800" : "text-emerald-600"}>{parteActual.nombre_parte}</span>
                    </p>
                  ) : (
                    <p className={`text-base sm:text-lg font-black uppercase tracking-wide text-center max-w-md leading-snug ${
                      esObligatorio ? "text-primary" : "text-emerald-700"
                    }`} style={{ overflowWrap: "break-word", wordBreak: "normal" }}>
                      {etiqueta}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <p className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider mt-1 sm:mt-2">
              Formatos admitidos: <span className="text-slate-600 font-black">{tiposPermitidos.join(", ")}</span>
            </p>

            <div className="relative w-full max-w-xl mx-auto">
              {files.length > 0 &&
                files.map((file, idx) => (
                  <motion.div
                    key={`file-${idx}`}
                    layoutId={idx === 0 ? "file-upload" : "file-upload-" + idx}
                    className="relative overflow-hidden z-40 bg-slate-50/50 border border-slate-200/60 flex flex-col items-start justify-start p-4 mt-4 w-full mx-auto rounded-2xl shadow-sm hover:border-slate-300 transition-all"
                  >
                    <div className="flex justify-between w-full items-center gap-4">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-lg">📄</span>
                        <motion.p
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          layout
                          className="text-sm font-black text-slate-800 truncate max-w-xs md:max-w-md"
                        >
                          {file.name}
                        </motion.p>
                      </div>
                      {partes[idx] && (
                        <span className="bg-green-50 text-green-700 text-[8px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg border border-green-200">
                          {partes[idx].nombre_parte} ✓
                        </span>
                      )}
                    </div>

                    <div className="flex text-[10px] font-bold md:flex-row flex-col items-start md:items-center w-full mt-3 justify-between text-slate-400 uppercase tracking-wider">
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        layout
                        className="px-2 py-0.5 rounded-md bg-white border border-slate-100 text-slate-500 font-bold"
                      >
                        {file.type}
                      </motion.p>

                      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} layout className="mt-1 md:mt-0 font-medium">
                        Fecha: {new Date(file.lastModified).toLocaleDateString()}
                      </motion.p>
                    </div>
                  </motion.div>
                ))}

              {files.length > 0 &&
                (isMultiLoad || (partes.length > 1 && files.length < partes.length)) && (
                  <div
                    className="mt-6 inline-flex items-center justify-center gap-2 px-6 h-11 bg-primary text-white hover:bg-primary/90 rounded-xl text-xs font-black uppercase tracking-widest transition-all duration-200 active:scale-95 shadow-lg shadow-primary/20 cursor-pointer mx-auto flex"
                  >
                    <IconUpload className="h-4 w-4 text-white" />
                    <span>
                      {isMultiLoad
                        ? "+ Agregar otro archivo"
                        : `Subir ${partes[files.length]?.nombre_parte || "siguiente parte"}`
                      }
                    </span>
                  </div>
                )}

              {!files.length && (
                <motion.div
                  layoutId="file-upload"
                  variants={mainVariant}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className={cn(
                    "relative group-hover/file:shadow-2xl z-40 bg-white flex items-center justify-center h-20 sm:h-32 mt-2 sm:mt-4 w-full max-w-[8rem] mx-auto rounded-2xl border border-slate-100",
                    "shadow-[0px_10px_50px_rgba(0,0,0,0.05)]"
                  )}
                >
                  {isDragActive ? (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-neutral-600 flex flex-col items-center text-xs"
                    >
                      Suelta aquí
                      <IconUpload className="h-4 w-4 text-neutral-600" />
                    </motion.p>
                  ) : (
                    <IconUpload className="h-5 w-5 text-slate-400 group-hover/file:text-primary transition-colors duration-200" />
                  )}
                </motion.div>
              )}

              {!files.length && (
                <motion.div
                  variants={secondaryVariant}
                  className="absolute opacity-0 border border-dashed border-primary/20 inset-0 z-30 bg-transparent flex items-center justify-center h-20 sm:h-32 mt-2 sm:mt-4 w-full max-w-[8rem] mx-auto rounded-2xl"
                ></motion.div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {alerta && (
          <Alerta
            tipo={alerta.tipo}
            mensaje={alerta.mensaje}
            onClose={() => setAlerta(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
